import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useLocation, useNavigate, Link } from "react-router-dom";

const VendorTransactions = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const vendor = location.state?.vendor;
  const server_url = localStorage.getItem("server_url");
  const company_id = localStorage.getItem("company_id") || "2";
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const grage_id = userData.grage_id || "1";
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [currentOutstanding, setCurrentOutstanding] = useState(0);
  // Form state
  const [formData, setFormData] = useState({
    vendor_id: vendor?.vendor_id || "",
    amount: "",
    transaction_type: "2", // Static 2 = OUT
    transaction_date: new Date().toISOString().split("T")[0],
    remark: "",
    available_balance: 0,
  });
  // State for delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    transactionId: null,
    transactionAmount: 0,
  });
  // Use refs to track component mounting
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    if (vendor) {
      setFormData((prev) => ({
        ...prev,
        vendor_id: vendor.vendor_id,
      }));
      setCurrentOutstanding(parseFloat(vendor.vendor_outstanding || 0));
      loadTransactions();
    }
    return () => {
      isMounted.current = false;
    };
  }, [vendor]);

  const loadTransactions = async () => {
    if (!vendor || !isMounted.current) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("vendor_id", vendor.vendor_id);
      formData.append("from_date", "2000-01-01");
      formData.append("to_date", new Date().toISOString().split("T")[0]);
      formData.append("action", "view_transactions");
      formData.append("transaction_grage_id", grage_id);
      const response = await axios.post(
        `${server_url}transaction.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      if (isMounted.current && response.data.status == "1") {
        setTransactions(response.data.result || []);
      } else if (isMounted.current && response.data.status !== "1") {
        toast.error(response.data.error || "Failed to load transactions");
        setTransactions([]);
      }
    } catch (error) {
      console.error("Error loading transactions:", error);
      if (isMounted.current) {
        toast.error("Unable to load transactions");
        setTransactions([]);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // If amount is changed, calculate new available balance (static type=2 OUT: reduce outstanding)
    if (name === "amount") {
      const amount = parseFloat(value) || 0;
      const newBalance = currentOutstanding - amount;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        available_balance: newBalance,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const openConfirmPopup = () => {
    if (!formData.amount || !formData.transaction_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    setShowConfirmPopup(true);
  };

  const closeConfirmPopup = () => {
    setShowConfirmPopup(false);
  };

  const handleSubmit = async () => {
    if (!formData.amount || !formData.transaction_date || !isMounted.current) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const postData = new FormData();
      postData.append("company_id", company_id);
      postData.append("vendor_id", formData.vendor_id);
      postData.append("amount", formData.amount);
      postData.append("remark", formData.remark);
      postData.append("transaction_date", formData.transaction_date);
      postData.append("transaction_type", formData.transaction_type);
      postData.append("available_balance", formData.available_balance);
      postData.append("transaction_grage_id", grage_id);
      postData.append("expense_category_id", "");
      postData.append("action", "add_transaction");
      const response = await axios.post(
        `${server_url}transaction.php`,
        postData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      if (isMounted.current && response.data.status == "1") {
        toast.success("Transaction added successfully!");
        // Reset form
        setFormData({
          vendor_id: vendor?.vendor_id || "",
          amount: "",
          transaction_type: "2",
          transaction_date: new Date().toISOString().split("T")[0],
          remark: "",
          available_balance: 0,
        });
        // Reload transactions
        await loadTransactions();
        // Update current outstanding
        const newOutstanding = parseFloat(formData.available_balance);
        setCurrentOutstanding(newOutstanding);
      } else if (isMounted.current) {
        toast.error(response.data.error || "Failed to add transaction");
      }
    } catch (error) {
      console.error("Error adding transaction:", error);
      if (isMounted.current) {
        toast.error("Unable to add transaction");
      }
    } finally {
      if (isMounted.current) {
        setSubmitting(false);
        setShowConfirmPopup(false);
      }
    }
  };

  const openDeleteConfirm = (transactionId, amount) => {
    setDeleteConfirm({
      isOpen: true,
      transactionId,
      transactionAmount: amount,
    });
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirm({
      isOpen: false,
      transactionId: null,
      transactionAmount: 0,
    });
  };

  const handleDelete = async () => {
    if (!deleteConfirm.transactionId || !isMounted.current) return;
    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("transaction_id", deleteConfirm.transactionId);
      formData.append("action", "delete_transaction_with_rebalance");
      const response = await axios.post(
        `${server_url}transaction.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      if (isMounted.current && response.data.status == "1") {
        toast.success("Transaction deleted successfully!");
        // Update local state safely
        setTransactions((prev) =>
          prev.filter((t) => t.transaction_id !== deleteConfirm.transactionId)
        );
        // Reload to get updated balances
        await loadTransactions();
      } else if (isMounted.current) {
        toast.error(response.data.error || "Failed to delete transaction");
      }
    } catch (error) {
      console.error("Error deleting transaction:", error);
      if (isMounted.current) {
        toast.error("Unable to delete transaction");
      }
    } finally {
      if (isMounted.current) {
        closeDeleteConfirm();
      }
    }
  };

  const formatCurrency = (amount) => {
    return `₹${parseInt(amount).toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (!vendor) {
    return (
      <div
        className="container"
        style={{ textAlign: "center", padding: "40px" }}
      >
        <h3>No vendor selected</h3>
        <p>Please go back and select a vendor to view transactions.</p>
        <Link
          to="/vendors"
          style={{ color: "#0352a1", textDecoration: "none" }}
        >
          <i className="fas fa-arrow-left"></i> Back to Vendors
        </Link>
      </div>
    );
  }

  return (
    <div
      className="container"
      style={{ maxWidth: "800px", margin: "0 auto", padding: "10px" }}
    >
      {/* Header with Vendor Info */}
      <div
        style={{
          backgroundColor: "#fff",
          padding: "15px",
          borderRadius: "10px",
          marginBottom: "20px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "10px",
          }}
        >
          <div>
            <h2 style={{ margin: 0, color: "#22543d" }}>
              {vendor.vendor_firm_name || "Vendor"}
            </h2>
            <p
              style={{
                margin: "5px 0 0 0",
                color: "#4a5568",
                fontSize: "14px",
              }}
            >
              Contact: {vendor.vendor_name || "N/A"} | Mobile:{" "}
              {vendor.vendor_mobile || "N/A"}
            </p>
          </div>
        </div>
        <div
          style={{
            backgroundColor: "#f0fff4",
            border: "1px solid #9ae6b4",
            borderRadius: "8px",
            padding: "12px",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "14px", color: "#4a5568" }}>
            Current Outstanding Balance
          </div>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color:
                currentOutstanding > 0
                  ? "#dc2626"
                  : currentOutstanding < 0
                  ? "#f59e0b"
                  : "#10b981",
            }}
          >
            {formatCurrency(currentOutstanding)}
          </div>
        </div>
      </div>
      {/* Add Transaction Form */}
      <div
        style={{
          backgroundColor: "#fff",
          padding: "15px",
          borderRadius: "10px",
          marginBottom: "20px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <h3 style={{ margin: "0 0 15px 0", color: "#444" }}>
          Add New Transaction
        </h3>
        <div
          style={{
            gridTemplateColumns: "1fr 1fr",
            gap: "10px",
            marginBottom: "15px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "600",
                color: "#555",
                fontSize: "14px",
              }}
            >
              Amount *
            </label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              required
              step="0.01"
              min="0.01"
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "5px",
                fontSize: "14px",
              }}
              placeholder="Enter amount"
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "600",
                color: "#555",
                fontSize: "14px",
              }}
            >
              Date *
            </label>
            <input
              type="date"
              name="transaction_date"
              value={formData.transaction_date}
              onChange={handleInputChange}
              required
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #ddd",
                borderRadius: "5px",
                fontSize: "14px",
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "600",
                color: "#555",
                fontSize: "14px",
              }}
            >
              New Balance
            </label>
            <div
              style={{
                padding: "8px",
                backgroundColor: "#f8f9fa",
                border: "1px solid #ddd",
                borderRadius: "5px",
                fontSize: "14px",
                textAlign: "center",
                fontWeight: "bold",
                color:
                  formData.available_balance > 0
                    ? "#dc2626"
                    : formData.available_balance < 0
                    ? "#f59e0b"
                    : "#10b981",
              }}
            >
              {formatCurrency(formData.available_balance)}
            </div>
          </div>
        </div>
        <div style={{ marginBottom: "15px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "600",
              color: "#555",
              fontSize: "14px",
            }}
          >
            Remarks
          </label>
          <textarea
            name="remark"
            value={formData.remark}
            onChange={handleInputChange}
            rows="2"
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ddd",
              borderRadius: "5px",
              fontSize: "14px",
              resize: "vertical",
            }}
            placeholder="Enter any remarks..."
          />
        </div>
        <button
          onClick={openConfirmPopup}
          disabled={submitting || !formData.amount}
          style={{
            backgroundColor: "#0352a1",
            color: "white",
            padding: "12px 30px",
            width: "100%",
            border: "none",
            borderRadius: "5px",
            fontSize: "16px",
            cursor: submitting || !formData.amount ? "not-allowed" : "pointer",
            opacity: submitting || !formData.amount ? 0.7 : 1,
            fontWeight: "600",
          }}
        >
          {submitting ? (
            <>
              <i
                className="fas fa-spinner fa-spin"
                style={{ marginRight: "8px" }}
              ></i>
              Adding...
            </>
          ) : (
            <>
              <i className="fas fa-plus" style={{ marginRight: "8px" }}></i>
              Add Transaction
            </>
          )}
        </button>
      </div>
      {/* Transactions Table */}
      <div
        style={{
          backgroundColor: "#fff",
          padding: "15px",
          borderRadius: "10px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px",
          }}
        >
          <h3 style={{ margin: 0, color: "#444" }}>All Transactions</h3>
          <div style={{ color: "#666", fontSize: "14px" }}>
            {transactions.length} transaction
            {transactions.length !== 1 ? "s" : ""}
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#0352a1", color: "white" }}>
                <th
                  style={{
                    padding: "12px 10px",
                    textAlign: "left",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Date
                </th>
                <th
                  style={{
                    padding: "12px 10px",
                    textAlign: "left",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Remark
                </th>
                <th
                  style={{
                    padding: "12px 10px",
                    textAlign: "left",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Credit
                </th>
                <th
                  style={{
                    padding: "12px 10px",
                    textAlign: "left",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Debit
                </th>
                <th
                  style={{
                    padding: "12px 10px",
                    textAlign: "left",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Available Balance
                </th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction, index) => (
                <tr
                  key={transaction.transaction_id}
                  style={{
                    borderBottom: "1px solid #e2e8f0",
                    backgroundColor: index % 2 === 0 ? "#fff" : "#f8f9fa",
                    cursor: "pointer",
                  }}
                
                >
                  <td
                    style={{
                      padding: "12px 10px",
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatDate(transaction.transaction_date)}
                  </td>
                  <td
                    style={{
                      padding: "12px 10px",
                      fontSize: "14px",
                    }}
                  >
                    {transaction.remark || "-"}
                  </td>
                  <td
                    style={{
                      padding: "12px 10px",
                      fontSize: "14px",
                      textAlign: "right",
                      color: "#10b981",
                      fontWeight: "600",
                    }}
                  >
                    {transaction.transaction_type === "1"
                      ? formatCurrency(parseFloat(transaction.amount))
                      : "-"}
                  </td>
                  <td
                    style={{
                      padding: "12px 10px",
                      fontSize: "14px",
                      textAlign: "right",
                      color: "#dc2626",
                      fontWeight: "600",
                    }}
                  >
                    {transaction.transaction_type === "2"
                      ? formatCurrency(parseFloat(transaction.amount))
                      : "-"}
                  </td>
                  <td
                    style={{
                      padding: "12px 10px",
                      fontSize: "14px",
                      textAlign: "right",
                      fontWeight: "600",
                      color:
                        parseFloat(transaction.available_balance) > 0
                          ? "#dc2626"
                          : parseFloat(transaction.available_balance) < 0
                          ? "#f59e0b"
                          : "#10b981",
                    }}
                  >
                    {formatCurrency(parseFloat(transaction.available_balance))}
                  </td>
                </tr>
              ))}
            </tbody>
            {/* Footer with totals */}
            <tfoot>
              <tr
                style={{
                  backgroundColor: "#f1f5f9",
                  borderTop: "2px solid #0352a1",
                }}
              >
                <td
                  colSpan="2"
                  style={{
                    padding: "12px 10px",
                    fontSize: "14px",
                    fontWeight: "600",
                    textAlign: "right",
                  }}
                >
                  Total:
                </td>
                <td
                  style={{
                    padding: "12px 10px",
                    fontSize: "14px",
                    textAlign: "right",
                    color: "#10b981",
                    fontWeight: "600",
                  }}
                >
                  {formatCurrency(
                    transactions.reduce((sum, t) => {
                      return t.transaction_type === "1"
                        ? sum + parseFloat(t.amount || 0)
                        : sum;
                    }, 0)
                  )}
                </td>
                <td
                  style={{
                    padding: "12px 10px",
                    fontSize: "14px",
                    textAlign: "right",
                    color: "#dc2626",
                    fontWeight: "600",
                  }}
                >
                  {formatCurrency(
                    transactions.reduce((sum, t) => {
                      return t.transaction_type === "2"
                        ? sum + parseFloat(t.amount || 0)
                        : sum;
                    }, 0)
                  )}
                </td>
                <td
                  style={{
                    padding: "12px 10px",
                    fontSize: "14px",
                    fontWeight: "600",
                    textAlign: "center",
                    color: "#475569",
                  }}
                >
                  {/* Empty cell for balance column */}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      {/* Add Transaction Confirmation Popup */}
      {showConfirmPopup && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "400px",
              padding: "24px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "60px",
                height: "60px",
                margin: "0 auto 20px",
                backgroundColor: "#d1fae5",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="fas fa-question-circle"
                style={{ fontSize: "28px", color: "#10b981" }}
              ></i>
            </div>
            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#1f2937",
              }}
            >
              Confirm Transaction
            </h3>
            <p
              style={{
                margin: "0 0 24px 0",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              Are you sure you want to add this transaction?
            </p>
            <div
              style={{
                backgroundColor: "#f8f9fa",
                padding: "15px",
                borderRadius: "8px",
                marginBottom: "20px",
              }}
            >
              <div style={{ marginBottom: "8px" }}>
                <span style={{ fontWeight: "600", color: "#555" }}>
                  Amount:{" "}
                </span>
                <span style={{ color: "#1f2937" }}>
                  {formatCurrency(parseFloat(formData.amount))}
                </span>
              </div>
              <div style={{ marginBottom: "8px" }}>
                <span style={{ fontWeight: "600", color: "#555" }}>Type: </span>
                <span
                  style={{
                    color: "#721c24",
                    backgroundColor: "#f8d7da",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "600",
                  }}
                >
                  OUT
                </span>
              </div>
              <div style={{ marginBottom: "8px" }}>
                <span style={{ fontWeight: "600", color: "#555" }}>
                  Remarks:{" "}
                </span>
                <span style={{ color: "#1f2937" }}>
                  {formData.remark || "-"}
                </span>
              </div>
              <div>
                <span style={{ fontWeight: "600", color: "#555" }}>
                  New Balance:{" "}
                </span>
                <span
                  style={{
                    color:
                      formData.available_balance > 0
                        ? "#dc2626"
                        : formData.available_balance < 0
                        ? "#f59e0b"
                        : "#10b981",
                    fontWeight: "bold",
                  }}
                >
                  {formatCurrency(formData.available_balance)}
                </span>
              </div>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={closeConfirmPopup}
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: "12px",
                  border: "2px solid #e2e8f0",
                  background: "white",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#0352a1",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {submitting ? (
                  <>
                    <i
                      className="fas fa-spinner fa-spin"
                      style={{ marginRight: "5px" }}
                    ></i>
                    Adding...
                  </>
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Popup */}
      {deleteConfirm.isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1001,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "400px",
              padding: "24px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "60px",
                height: "60px",
                margin: "0 auto 20px",
                backgroundColor: "#fee2e2",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="fas fa-exclamation-triangle"
                style={{ fontSize: "28px", color: "#dc2626" }}
              ></i>
            </div>
            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#1f2937",
              }}
            >
              Delete Transaction
            </h3>
            <p
              style={{
                margin: "0 0 24px 0",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              Are you sure you want to delete this transaction of{" "}
              {formatCurrency(deleteConfirm.transactionAmount)}? This will
              affect subsequent balances.
            </p>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={closeDeleteConfirm}
                style={{
                  flex: 1,
                  padding: "12px",
                  border: "2px solid #e2e8f0",
                  background: "white",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add Font Awesome for icons */}
    </div>
  );
};

export default VendorTransactions;
